/**
 * Read-only placeholder when redemption SQLite is not wired (API snapshot mode or bare {@link AppContainer}).
 * Export/sync callers see empty catalogs and lists; mutations return failures or throw where persistence is required.
 */
import { fail, type Result } from '@core/domain/utils/Result.ts';
import { RedencionBonoApplicationService } from '../../application/RedencionBonoApplicationService.ts';
import type { RedencionBonoRepositoryPort } from '../../domain/ports/RedencionBonoRepositoryPort.ts';
import type {
  RedencionBonoCatalogSnapshot,
  RedencionBonoHeader,
  RedencionBonoParticipanteFormPayload,
} from '../../domain/redencionBonoModel.ts';

const UNAVAILABLE =
  'La redención de bonos requiere base de datos local; no disponible en este modo.';

const EMPTY_REDENCION_CATALOG: RedencionBonoCatalogSnapshot = {
  entregas: [],
  gruposAlimentos: [],
  proveedores: [],
  tiposBonos: [],
};

const emptyRepo: RedencionBonoRepositoryPort = {
  listRedenciones: () => [],
  getRedencion: () => null,
  listParticipantesForRedencion: () => [],
  findRedencionIdForParticipante: () => null,
  findConflictingRedencionIdForParticipante: () => null,
  getCatalog: () => EMPTY_REDENCION_CATALOG,
  findParticipanteRedencionPrefillByDocumento: () => null,
  resolveParticipanteRedencionBusquedaPrefill: () => ({ ok: false, reason: 'not_found' }),
  resolveParticipanteRedencionBusquedaPrefillByParticipanteId: () => ({ ok: false, reason: 'not_found' }),
  listParticipanteBusquedaCandidates: () => [],
  insertRedencion: () => {
    throw new Error(UNAVAILABLE);
  },
  updateRedencion: () => {
    throw new Error(UNAVAILABLE);
  },
  getBonoNombreById: () => null,
  resolveBonoIdForRedencionCaptura: () => null,
  participanteTipoBonoCatalogId: () => null,
  listRedencionesUploadedOffline: () => [],
  countPendingOfflineExport: () => 0,
  deleteUploadedRedencion: () => {
    throw new Error(UNAVAILABLE);
  },
};

class RedencionBonoApplicationPlaceholder extends RedencionBonoApplicationService {
  constructor() {
    super(emptyRepo);
  }

  override async saveRedencion(
    _editingId: number | null,
    _header: RedencionBonoHeader,
    _participantes: RedencionBonoParticipanteFormPayload[],
  ): Promise<{ ok: true; id: number } | { ok: false; message: string }> {
    return { ok: false, message: UNAVAILABLE };
  }

  override deleteUploadedRedencion(_id: number): Result<void, string> {
    return fail('El almacenamiento local de redenciones no está disponible en este modo.');
  }
}

/** Factory for composition roots when no SqlJs redemption repository exists. */
export function createRedencionBonoApplicationPlaceholder(): RedencionBonoApplicationService {
  return new RedencionBonoApplicationPlaceholder();
}
